<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="pc-header">
        <span class="pc-bar pc-bar--xl"></span>
        <span class="pc-bar pc-bar--md"></span>
      </div>
      <div class="pc-grid">
        <div class="pc-col pc-col--pros">
          <header class="pc-col-head">
            <span class="material-symbols-rounded pc-col-icon">add</span>
            Pros
          </header>
          <div class="pc-notes">
            <div
              v-for="n in 3"
              :key="`pro-${n}`"
              class="pc-note pc-note--pro"
              :style="{ '--n': n - 1 }"
            >
              <span class="pc-bar pc-bar--lg"></span>
              <span class="pc-bar pc-bar--sm"></span>
            </div>
          </div>
        </div>
        <div class="pc-divider"></div>
        <div class="pc-col pc-col--cons">
          <header class="pc-col-head">
            <span class="material-symbols-rounded pc-col-icon">remove</span>
            Cons
          </header>
          <div class="pc-notes">
            <div
              v-for="n in 2"
              :key="`con-${n}`"
              class="pc-note pc-note--con"
              :style="{ '--n': n - 1 + 0.4 }"
            >
              <span class="pc-bar pc-bar--lg"></span>
              <span class="pc-bar pc-bar--sm"></span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="pc-phone-q"></span>
      <div class="pc-toggle">
        <button
          type="button"
          class="pc-toggle-btn"
          :class="{ '--active': side === 'pro' }"
          tabindex="-1"
        >
          <span class="material-symbols-rounded pc-toggle-icon">add</span>
          Pro
        </button>
        <button
          type="button"
          class="pc-toggle-btn"
          :class="{ '--active': side === 'con' }"
          tabindex="-1"
        >
          <span class="material-symbols-rounded pc-toggle-icon">remove</span>
          Con
        </button>
      </div>
      <div class="pc-input">
        <span class="pc-bar pc-bar--md"></span>
        <span class="pc-caret"></span>
      </div>
      <button type="button" class="pc-submit" tabindex="-1">
        Add
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewProsCons',
  components: { PreviewFrame },
  props: { slide: { type: Object as PropType<SlideType>, required: true } },
  data() {
    return { side: 'pro' as 'pro' | 'con' }
  },
})
</script>

<style scoped lang="scss">
.pc-bar {
  display: block; height: 6px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.pc-bar--xl { width: 70%; background: rgba(15, 15, 20, 0.2); height: 8px; }
.pc-bar--md { width: 50%; height: 7px; }
.pc-bar--lg { width: 80%; }
.pc-bar--sm { width: 50%; }

.pc-header {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.pc-grid {
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  gap: 8px;
  flex: 1;
}
.pc-col { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.pc-col-head {
  display: flex; align-items: center; gap: 4px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.pc-col--pros .pc-col-head { color: var(--accent); }
.pc-col--cons .pc-col-head { color: rgba(15, 15, 20, 0.55); }
.pc-col-icon {
  width: 16px; height: 16px;
  border-radius: 4px;
  display: grid; place-items: center;
  font-size: 12px;
}
.pc-col--pros .pc-col-icon { background: var(--accent); color: white; }
.pc-col--cons .pc-col-icon { background: rgba(15, 15, 20, 0.6); color: white; }

.pc-divider {
  background: rgba(15, 15, 20, 0.08);
  width: 1px;
}

.pc-notes { display: grid; gap: 5px; }
.pc-note {
  padding: 6px 8px;
  border-radius: 6px;
  display: flex; flex-direction: column; gap: 4px;
  animation: pc-slide-in 4s ease-out infinite;
  animation-delay: calc(var(--n) * 600ms);
  opacity: 0;
}
.pc-note--pro {
  background: color-mix(in srgb, var(--accent) 10%, white);
  border-left: 2px solid var(--accent);
}
.pc-note--con {
  background: rgba(15, 15, 20, 0.04);
  border-left: 2px solid rgba(15, 15, 20, 0.4);
}

/* phone */
.pc-phone-q {
  display: block; height: 8px; width: 70%;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
  margin-bottom: 2px;
}
.pc-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 3px;
  background: rgba(15, 15, 20, 0.04);
  border-radius: 8px;
}
.pc-toggle-btn {
  appearance: none;
  display: flex; align-items: center; justify-content: center; gap: 4px;
  padding: 6px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: rgba(15, 15, 20, 0.55);
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}
.pc-toggle-btn.--active {
  background: white;
  color: var(--accent);
  box-shadow: 0 1px 2px rgba(15, 15, 20, 0.1);
  animation: pc-active 4s ease-in-out infinite;
}
.pc-toggle-icon { font-size: 14px; }

.pc-input {
  position: relative;
  height: 36px;
  border-radius: 6px;
  border: 1px solid rgba(15, 15, 20, 0.15);
  padding: 0 8px;
  display: flex; align-items: center;
}
.pc-caret {
  position: absolute;
  right: 8px;
  width: 1px; height: 14px;
  background: var(--accent);
  animation: pc-caret 1s steps(2) infinite;
}
.pc-submit {
  appearance: none;
  padding: 7px 10px;
  border-radius: 6px;
  background: var(--accent);
  color: white;
  border: none;
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

@keyframes pc-slide-in {
  0%, 10%   { opacity: 0; transform: translateX(-8px); }
  25%, 90%  { opacity: 1; transform: translateX(0); }
  100%      { opacity: 1; transform: translateX(0); }
}
@keyframes pc-active {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.02); }
}
@keyframes pc-caret {
  0% { opacity: 1; }
  50% { opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .pc-note, .pc-toggle-btn.--active, .pc-caret { animation: none; }
  .pc-note { opacity: 1; transform: translateX(0); }
}
</style>
